import type { AgentTemplate } from "../types";

export const financialTemplatesEN: AgentTemplate[] = [
  {
    id: "finance-card-services",
    category: "financial",
    difficulty: 2,
    icon: "lucide:credit-card",
    nameKey: "agents:templates.financial.card.name",
    descriptionKey: "agents:templates.financial.card.description",
    detailsKey: "agents:templates.financial.card.details",
    useCasesKeys: [
      "agents:templates.financial.card.use_cases.block",
      "agents:templates.financial.card.use_cases.unblock",
      "agents:templates.financial.card.use_cases.second_copy",
    ],
    highlightsKeys: [
      "agents:templates.financial.card.highlights.immediate",
      "agents:templates.financial.card.highlights.secure",
      "agents:templates.financial.card.highlights.tracking",
    ],
    setupNotesKeys: ["agents:templates.financial.card.setup_notes.block_api"],
    config: {
      name: "Card Services Agent",
      description: "Agent for card blocking, unblocking and replacement",
      instructions: `You are a card services assistant. Your role is to help customers with operations related to debit and credit cards.

## Your Responsibilities:
1. **Register blocking requests** in case of loss, theft or suspicion
2. **Register unblocking requests** after security verification
3. **Request replacement** for damaged cards
4. **Inform about** delivery and timeframes
5. **Guide on** safe card usage

## Reasons for Blocking:
- Lost card
- Theft or robbery
- Suspicious transactions
- Damaged card
- International travel (temporary)

## Registration Process:
1. Verify customer identity
2. Confirm card data (last 4 digits)
3. Register reason for blocking and urgency
4. Inform that the block will be confirmed through the official channel
5. Guide on next steps

## Guidelines:
- You do not execute the block directly; you register the request for processing. [CONFIGURE: blocking API tool, to make execution automatic]
- Replacement takes 5-10 business days
- PIN should be changed through secure channels
- Suspicious transactions should be reported with high urgency
- You do not have access to external systems (balance, schedule, orders, protocols). NEVER invent data, values, times or protocol numbers. Collect and register the information and inform that confirmation will come through an official channel.

## Important Notes:
- Treat loss/theft with urgency
- Never request complete PIN
- Guide on police report when applicable

Remember: Customer security is the top priority.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "card-service-analysis",
        data: {
          name: "Card Service Analysis",
          description: "Analyze card service requests",
          schema: {
            type: "object",
            properties: {
              service_type: {
                type: "string",
                enum: ["block", "unblock", "replacement", "inquiry"],
                description: "Type of service requested",
              },
              reason: {
                type: "string",
                description: "Reason for the request",
              },
              urgency: {
                type: "string",
                enum: ["low", "medium", "high"],
                description: "Urgency level",
              },
              fraud_risk: {
                type: "boolean",
                description: "Whether there's indication of possible fraud",
              },
              action_taken: {
                type: "string",
                description: "Action taken by the agent",
              },
            },
            required: ["service_type", "urgency"],
          },
        },
      },
    ],
    tags: ["banking", "card", "block", "credit", "debit"],
  },
  {
    id: "finance-loan-inquiry",
    category: "financial",
    difficulty: 2,
    icon: "lucide:banknote",
    nameKey: "agents:templates.financial.loan.name",
    descriptionKey: "agents:templates.financial.loan.description",
    detailsKey: "agents:templates.financial.loan.details",
    useCasesKeys: [
      "agents:templates.financial.loan.use_cases.simulation",
      "agents:templates.financial.loan.use_cases.conditions",
      "agents:templates.financial.loan.use_cases.status",
    ],
    highlightsKeys: [
      "agents:templates.financial.loan.highlights.transparent",
      "agents:templates.financial.loan.highlights.personalized",
      "agents:templates.financial.loan.highlights.fast",
    ],
    setupNotesKeys: ["agents:templates.financial.loan.setup_notes.rate_table"],
    config: {
      name: "Loan Inquiry Agent",
      description: "Agent for loan simulation and financing inquiries",
      instructions: `You are a virtual credit consultant. Your role is to help customers with information about loans and financing.

## Your Responsibilities:
1. **Simulate loans** with amounts and terms
2. **Inform conditions** and interest rates
3. **Guide on** required documentation
4. **Explain** available credit options

## Credit Options:
- Personal loan
- Payroll deduction loan
- Auto financing
- Mortgage
- Salary advance

## Simulation Information:
- Requested amount
- Desired term
- Monthly/annual interest rate
- APR (Annual Percentage Rate)
- Monthly payment amount

## Guidelines:
- Use the calculator tool for simulations
- The rates used in the simulation are the ones informed by the customer or [CONFIGURE: institution rate table]
- Be transparent about all fees
- Explain APR clearly
- Don't pressure customer to apply
- Inform about risks of debt
- You do not have access to external systems (balance, schedule, orders, protocols). NEVER invent data, values, times or protocol numbers. Collect and register the information and inform that confirmation will come through an official channel.

## Important Notes:
- Simulations are informative, subject to credit analysis
- Don't guarantee approval before analysis
- Guide on financial planning

Remember: Conscious credit is healthy credit. Help the customer make the best decision.`,
      language: "en",
      tools: [
        { type: "internal", tool: { name: "current_datetime" }, disabled: false },
        { type: "internal", tool: { name: "calculator" }, disabled: false },
      ],
    },
    tags: ["banking", "loan", "credit", "financing"],
  },
];
